import type { BuildContext, Fragment } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { card } from "../primitives/card.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { featureSchema, type FeatureProps } from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { assemble } from "./shared.js";

export function featureDurationSec(props: FeatureProps): number {
  return 1 + props.benefits.length * 1.5 + 4;
}

export function buildFeatureShowcaseHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = featureSchema.parse(rawProps) as FeatureProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const totalSec = featureDurationSec(props);
  const fullCtx: BuildContext = { ...ctx, durationSec: totalSec };

  const titleSize = scaleFor(ctx.format, { base: 84, short: 96, square: 76, wide: 90 });
  const benefitSize = scaleFor(ctx.format, { base: 44, short: 50, square: 40, wide: 46 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  const bg = backgroundGradient(fullCtx, "bg", { variant: "primary", animated: true });

  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });
  const titleFrag = hookText({ id: "tpl-title", text: props.title, startSec: 0, size: titleSize, color: "#fff" });

  const benefitFragments: Fragment[] = [];
  const benefitHtmls: string[] = [];
  props.benefits.forEach((b, i) => {
    const startSec = (20 + i * 22) / 30;
    const durSec = Math.max(0.5, totalSec - startSec);
    const id = `tpl-benefit-${i}`;
    const cardInner = `<div style="display:flex;align-items:center;gap:24px;">
  <div style="width:${benefitSize * 1.4}px;height:${benefitSize * 1.4}px;border-radius:9999px;background:${colors.accent};display:flex;align-items:center;justify-content:center;color:${colors.bg};font-weight:900;font-size:${Math.round(benefitSize * 0.9)}px;">✓</div>
  <div style="font-weight:700;font-size:${benefitSize}px;color:white;">${escapeHtml(b)}</div>
</div>`;
    const f = card({ id, childrenHtml: cardInner, startSec, padding: 32, variant: "filled" });
    benefitFragments.push(f);
    benefitHtmls.push(
      `<div id="${id}-clip" class="clip" data-start="${startSec}" data-duration="${durSec}" data-track-index="${i + 1}">${f.html}</div>`,
    );
  });

  const ctaStartSec = (20 + props.benefits.length * 22 + 30) / 30;
  const ctaDur = Math.max(2, totalSec - ctaStartSec);
  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStartSec, durationSec: ctaDur, size: ctaSize });

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div style="margin-top:24px;">
  <div id="tpl-badge" style="display:inline-flex;padding:10px 24px;border-radius:9999px;background:${colors.accent}33;border:1px solid ${colors.accent};color:${colors.accent};font-weight:800;font-size:28px;letter-spacing:2px;text-transform:uppercase;margin-bottom:24px;">${escapeHtml(props.feature)}</div>
  ${titleFrag.html}
</div>
<div id="tpl-benefits" style="display:flex;flex-direction:column;gap:20px;margin-top:36px;flex:1;">
${benefitHtmls.join("\n")}
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStartSec}" data-duration="${ctaDur}" data-track-index="${props.benefits.length + 2}" style="display:flex;justify-content:center;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "FeatureShowcase",
    ctx: fullCtx,
    background: bg,
    contentFragments: [logoFrag, titleFrag, ...benefitFragments, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
